import { type SessionEndReason, safeReturnPath } from "@esi/shared";
import { useEffect, useState, useSyncExternalStore } from "react";

/**
 * When the session ends mid-use (an API call answers 401 and the session check confirms it, the
 * visible-tab poll finds it, or another tab says it signed out), the app goes to the sign-in
 * page and back to the current page afterwards. With something to lose (a message being typed,
 * an upload or an edit in progress) it never leaves by itself, whatever the reason: a banner
 * stays until the person signs in again, copies their text or dismisses it.
 */

let ended = false;
/** When the session was found ended (ms since epoch); 0 while it is live. */
let endedAt = 0;
/** Why it ended, as the session check or another tab said. */
let reason: SessionEndReason = "expired";
/** This tab is signing out: nothing may race its navigation to the IdP's logout. */
let signingOut = false;
/** The person closed the banner; it comes back on the next blocked change or send, or a new end. */
let dismissed = false;
/** Bumped when a 401 turned out to be a stale token on a live session (signed in elsewhere). */
let renewals = 0;
/** Screens with work that a page change would lose, by registration, with their text if any. */
const unsaved = new Map<symbol, string>();
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) {
    listener();
  }
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Tabs of this browser tell each other when the session ends (a sign-out in one tab ends it
 * in all). Absent in some browsers: the visible-tab poll (`useSessionWatch`) catches it then.
 */
const CHANNEL = "esi-session";
const channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(CHANNEL) : null;
channel?.addEventListener("message", (event: MessageEvent) => {
  const data = event.data as { type?: unknown; reason?: unknown } | null;
  if (data?.type === "ended" && isReason(data.reason)) {
    markSessionEnded(data.reason);
  }
});

const isReason = (value: unknown): value is SessionEndReason =>
  value === "signed_out" || value === "revoked" || value === "expired";

/** BroadcastChannel is available: other tabs hear of an ended session at once. */
export const sessionBroadcastAvailable = () => channel !== null;

/** Tells the other tabs that this browser's session ended (a sign-out here). */
export function broadcastSessionEnded(why: SessionEndReason): void {
  try {
    channel?.postMessage({ type: "ended", reason: why });
  } catch {
    // A closed channel: the other tabs' poll will notice.
  }
}

export function markSessionEnded(why: SessionEndReason = "expired"): void {
  if (!ended && !signingOut) {
    ended = true;
    endedAt = Date.now();
    reason = why;
    dismissed = false;
    notify();
  }
}

/** Ended: no API call goes out but the session check (api.ts). */
export const isSessionEnded = () => ended;

export const sessionEndReason = () => reason;
export const useSessionEndReason = () => useSyncExternalStore(subscribe, () => reason);

/** When the session was found ended, 0 while live. */
export const sessionEndedAt = () => endedAt;

/** A deliberate sign-out here: no session check or sign-in redirect may override it. */
export function markSigningOut(): void {
  signingOut = true;
}
export const isSigningOut = () => signingOut;

/** A 401 on a session that is still live (a CSRF token rotated by a sign-in in another tab). */
export function markSessionRenewed(): void {
  // Signed in again (here or in another tab): the session is live, nothing has ended.
  ended = false;
  endedAt = 0;
  dismissed = false;
  renewals++;
  notify();
}

/** The session is live again (a session check said so): nothing has ended. */
export function resetSessionEnded(): void {
  ended = false;
  endedAt = 0;
  reason = "expired";
  signingOut = false;
  dismissed = false;
  notify();
}

/** Hides the banner until the next blocked change or send, or a new end; nothing else changes. */
export function dismissSessionEnded(): void {
  dismissed = true;
  notify();
}

/** The person tried to change something: a dismissed banner, the only way to sign in, returns. */
export function showSessionEnded(): void {
  if (ended && dismissed) {
    dismissed = false;
    notify();
  }
}
export const useSessionEndedDismissed = () => useSyncExternalStore(subscribe, () => dismissed);

export const useSessionRenewals = () => useSyncExternalStore(subscribe, () => renewals);

export const useSessionEnded = () => useSyncExternalStore(subscribe, () => ended);
export const hasUnsavedWork = () => unsaved.size > 0;
export const useHasUnsavedWork = () => useSyncExternalStore(subscribe, hasUnsavedWork);

/**
 * Declares, while `active`, that leaving the page would lose the caller's work. `text` is what
 * the person typed, which the banner offers to copy once the session has ended.
 */
export function useUnsavedWork(active: boolean, text = ""): void {
  const [token] = useState(() => Symbol("unsaved"));
  useEffect(() => {
    if (!active) {
      return;
    }
    return () => {
      unsaved.delete(token);
      notify();
    };
  }, [active, token]);
  useEffect(() => {
    if (!active || unsaved.get(token) === text) {
      return;
    }
    const added = !unsaved.has(token);
    unsaved.set(token, text);
    // Keystrokes matter to the banner only: no notify for them while the session is live.
    if (added || ended) {
      notify();
    }
  }, [active, token, text]);
}

/** The unsaved text of every screen that has some, one block per screen ("" when none). */
export function unsavedText(): string {
  return [...unsaved.values()]
    .map((text) => text.trim())
    .filter((text) => text !== "")
    .join("\n\n");
}
export const useUnsavedText = () => useSyncExternalStore(subscribe, unsavedText);

/** A same-origin path to come back to (the server applies the same rule), else home. */
export const safeReturnTo = (path: string): string => safeReturnPath(path);

/**
 * ESI's sign-in page, at `path` (the page signed-out visitors see there), with a notice of why
 * the session ended; signing in comes back to `path`.
 */
export function signInHref(path: string, why: SessionEndReason = "expired"): string {
  const url = new URL(safeReturnTo(path), "https://esi.invalid");
  url.searchParams.set("auth_notice", why);
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The current page as a return path, without a previous sign-in error or notice. */
export function currentPath(): string {
  const search = new URLSearchParams(window.location.search);
  search.delete("auth_error");
  search.delete("auth_notice");
  const qs = search.toString();
  return `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
}

/**
 * Where a sign-in on this page comes back to: `/signin?returnTo=…` names it (a native client's
 * authorization, which the server checks again); any other page comes back to itself.
 */
export function signInReturnPath(): string {
  if (window.location.pathname === "/signin") {
    return safeReturnTo(new URLSearchParams(window.location.search).get("returnTo") ?? "/");
  }
  return safeReturnTo(currentPath());
}

/** Leaves for the sign-in flow (a full page load); replaceable in tests. */
export const signInNavigation = {
  go: (href: string) => window.location.assign(href),
};
